// React Router DOM v6 – ROTAS COMPLETAS
import React from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

// Guards / Layouts
import StoreManagement from '@/pages/StoreManagement';
import ImportsReports from '@/pages/ImportsReports';
import Invoice from '@/pages/Invoice';
import CreateTrustee from '@/pages/Trustee/CreateTrustee';
import UpdateTrustee from '@/pages/Trustee/UpdateTrustee';
import PlanogramAutomation from '@/pages/PlanogramAutomation';
import PlanogramQuickAdjust from '@/pages/PlanogramQuickAdjust';
import ListTrustee from '@/pages/Trustee/ListTrustee';
import TransferValidation from '@/pages/Transfer/TransferValidation';
import TaxDataShopManager from '@/pages/TaxDataShopManager';
import TotemsManagement from '@/pages/Totems/TotemsManagement';
import LogInRoute from '../helpers/routes/LogInRoute';
import PublicRoute from '../helpers/routes/PublicRoute';
import PrivateRoute from '../helpers/routes/PrivateRoute';
import FlagRoute from '../helpers/routes/FlagRoute';
import { EnRoles } from '../interfaces/Roles';

const NfeReemission = React.lazy(() => import('../pages/NFe'));

export type AppRouteObject = RouteObject & {
    role?: EnRoles[];
};

// ======================
// Lazy Pages
// ======================

// Auth
const Login = React.lazy(() => import('../pages/Authentication/LogIn'));
const ForgotPassword = React.lazy(
    () => import('../pages/Authentication/ForgotPassword'),
);
const AccountValidation = React.lazy(
    () => import('../pages/Authentication/AccountValidation'),
);
const ResetPassword = React.lazy(
    () => import('../pages/Authentication/ResetPassword'),
);
const SignUp = React.lazy(() => import('../pages/Authentication/SignUp'));

// Users
const ListAdmin = React.lazy(() => import('../pages/Users/Admin/ListAdmin'));
const CreateAdmin = React.lazy(
    () => import('../pages/Users/Admin/CreateAdmin'),
);
const UpdateAdmin = React.lazy(
    () => import('../pages/Users/Admin/UpdateAdmin'),
);

const ListShopManager = React.lazy(
    () => import('../pages/Users/ShopManager/ListShopManager'),
);
const CreateShopManager = React.lazy(
    () => import('../pages/Users/ShopManager/CreateShopManager'),
);
const UpdateShopManager = React.lazy(
    () => import('../pages/Users/ShopManager/UpdateShopManager'),
);

const ListClient = React.lazy(() => import('../pages/Users/Client/ListClient'));
const CreateClient = React.lazy(
    () => import('../pages/Users/Client/CreateClient'),
);
const UpdateClient = React.lazy(
    () => import('../pages/Users/Client/UpdateClient'),
);

// Stores
const ListStore = React.lazy(() => import('../pages/Store/ListStore'));
const CreateStore = React.lazy(() => import('../pages/Store/CreateStore'));
const StoreDetailsTabs = React.lazy(
    () => import('../pages/Store/DetailsStore/Tabs'),
);

// Products
const ListProduct = React.lazy(() => import('../pages/Product/ListProduct'));
const CreateProduct = React.lazy(
    () => import('../pages/Product/CreateProduct'),
);
const UpdateProduct = React.lazy(
    () => import('../pages/Product/UpdateProduct'),
);

const ListProductGlobal = React.lazy(
    () => import('../pages/ProductGlobal/ListProduct'),
);
const CreateProductGlobal = React.lazy(
    () => import('../pages/ProductGlobal/CreateProduct'),
);
const UpdateProductGlobal = React.lazy(
    () => import('../pages/ProductGlobal/UpdateProduct'),
);

const ReportsProducts = React.lazy(() => import('../pages/ReportProducts'));

// Categories
const ListCategory = React.lazy(() => import('../pages/Category/ListCategory'));
const CreateCategory = React.lazy(
    () => import('../pages/Category/CreateCategory'),
);
const UpdateCategory = React.lazy(
    () => import('../pages/Category/UpdateCategory'),
);

const ListCategoryGlobal = React.lazy(
    () => import('../pages/CategoriesGlobal/ListCategory'),
);
const CreateCategoryGlobal = React.lazy(
    () => import('../pages/CategoriesGlobal/CreateCategory'),
);
const UpdateCategoryGlobal = React.lazy(
    () => import('../pages/CategoriesGlobal/UpdateCategory'),
);

// Providers
const ListProvider = React.lazy(
    () => import('../pages/Users/Supplier/ListProvider'),
);
const CreateProvider = React.lazy(
    () => import('../pages/Users/Supplier/CreateProvider'),
);
const UpdateProvider = React.lazy(
    () => import('../pages/Users/Supplier/UpdateProvider'),
);

const CreateProviderGlobal = React.lazy(
    () => import('../pages/ProvidersGlobal/CreateProvider'),
);

const ListProvidersGlobal = React.lazy(
    () => import('../pages/ProvidersGlobal/ListProvider'),
);

const UpdateProvidersGlobal = React.lazy(
    () => import('../pages/ProvidersGlobal/UpdateProvider'),
);

// Adverts
const ListAdvert = React.lazy(() => import('../pages/Advert/ListAdvert'));
const CreateAdvert = React.lazy(() => import('../pages/Advert/CreateAdvert'));
const UpdateAdvert = React.lazy(() => import('../pages/Advert/UpdateAdvert'));

const ListAdvertAdmin = React.lazy(
    () => import('../pages/AdvertAdmin/ListAdvert'),
);
const CreateAdvertAdmin = React.lazy(
    () => import('../pages/AdvertAdmin/CreateAdvert'),
);
const UpdateAdvertAdmin = React.lazy(
    () => import('../pages/AdvertAdmin/UpdateAdvert'),
);

// Financial
const Financial = React.lazy(() => import('../pages/Financial'));
const CreateFinancialRegister = React.lazy(
    () => import('../pages/Financial/CreateFinancialRegister'),
);
const UpdateFinancialRegister = React.lazy(
    () => import('../pages/Financial/UpdateFinancialRegister'),
);

// Wallet
const WalletTabs = React.lazy(() => import('../pages/Wallet/tabs'));

// Notifications
const NotificationListV2 = React.lazy(
    () => import('../pages/PushNotificationV2/NotificationList'),
);
const CreateNotificationV2 = React.lazy(
    () => import('../pages/PushNotificationV2/CreateNotification'),
);
const UpdateNotificationV2 = React.lazy(
    () => import('../pages/PushNotificationV2/UpdateNotification'),
);
const NotificationLogs = React.lazy(
    () => import('../pages/PushNotificationV2/NotificationLogs'),
);

// Misc
const Settings = React.lazy(() => import('../pages/Settings'));
const BackendSettings = React.lazy(() => import('../pages/BackendSettings'));
const Announcements = React.lazy(() => import('../pages/Announcements'));
const AnnouncementView = React.lazy(
    () => import('../pages/Announcements/ViewShopManager'),
);

const TaxRegime = React.lazy(
    () => import('../components/TaxFiscal/TaxFiscalV2'),
);

const ListAdjustments = React.lazy(
    () => import('../pages/Stock/ListAdjustments'),
);

const ListSale = React.lazy(() => import('../pages/Sale/ListSale'));

const ListStock = React.lazy(() => import('../pages/Stock/ListStock'));

const StockBreakage = React.lazy(() => import('../pages/Stock/BreakAge'));

const StockTurnover = React.lazy(() => import('../pages/Stock/Turnover'));

const CreateOrUpdateAnnouncement = React.lazy(
    () => import('../pages/Announcements/CreateOrUpdate'),
);

const ShowAndUpdateUser = React.lazy(
    () => import('../pages/MyProfile/ShowAndUpdateUser'),
);

const CreateTransfer = React.lazy(
    () => import('../pages/Transfer/CreateTransfer'),
);
const CreateStoreTransfer = React.lazy(
    () => import('../pages/Store/DetailsStore/Tabs/Transfers/CreateTransfer'),
);
const CreatePickList = React.lazy(
    () => import('../pages/Transfer/CreatePickList'),
);
const UpdateTransfer = React.lazy(
    () => import('../pages/Transfer/UpdateTransfer'),
);
const UpdatePicklist = React.lazy(() => import('../components/UpdatePicklist'));

const CreatePurchase = React.lazy(
    () => import('../pages/Store/DetailsStore/Tabs/Purchase/CreatePurchase'),
);

const UpdatePurchase = React.lazy(
    () => import('../components/purchase/UpdatePurchase'),
);

const ListPurchases = React.lazy(
    () => import('../pages/Purchase/ListPurchases'),
);

const ListTransfer = React.lazy(() => import('../pages/Transfer/ListTransfer'));

const CreatePicklist = React.lazy(
    () => import('../pages/Store/DetailsStore/Tabs/Transfers/CreatePicklist'),
);

const UpdateShopManagerV2 = React.lazy(
    () => import('../pages/Users/ShopManagerV2/UpdateShopManager'),
);

const CreateShopManagerV2 = React.lazy(
    () => import('../pages/Users/ShopManagerV2'),
);

const DashboardComponentV3 = React.lazy(() => import('../pages/Dashboard'));

const QuickActions = React.lazy(() => import('../pages/QuickActions'));
const QuickActionsNFE = React.lazy(
    () => import('../pages/QuickActions/QuickActionNFE/NFE'),
);
const QuickActionsImportNFE = React.lazy(
    () => import('../pages/QuickActions/QuickActionNFE/NFEImport'),
);

const QuickActionsInventory = React.lazy(
    () => import('../pages/QuickActionsInventory/pages/InventoryList'),
);

const InventoryAdd = React.lazy(
    () => import('../pages/QuickActionsInventory/pages/InventoryAdd'),
);

const InventoryReport = React.lazy(
    () => import('../pages/QuickActionsInventory/pages/InventoryReport'),
);

const Charges = React.lazy(() => import('../pages/Charges'));

const ChargesCreateOrUpdate = React.lazy(
    () => import('../pages/Charges/CreateOrUpdate'),
);

const Churn = React.lazy(() => import('../pages/Churn'));

const PromotionsAndCoupons = React.lazy(
    () => import('../pages/PromotionsAndCoupons'),
);

const Clients = React.lazy(() => import('../pages/Clients'));

// ======================
// Roles
// ======================
const onlySysAdmin = [EnRoles.sysAdmin];
const adminRole = [EnRoles.admin, EnRoles.roAdmin, EnRoles.sysAdmin];
const allAdminsWithoutRoAdminRole = [EnRoles.admin, EnRoles.sysAdmin];
export const shopManagerRole = [EnRoles.shopManager];
const myProfileRoles = [
    EnRoles.admin,
    EnRoles.roAdmin,
    EnRoles.sysAdmin,
    EnRoles.shopManager,
];

// ======================
// Routes
// ======================
export const routes = (): RouteObject[] => {
    return [
        {
            element: <LogInRoute />,
            children: [{ path: '/login', element: <Login /> }],
        },
        /**
         * Private Role
         */
        {
            element: <PrivateRoute />,
            children: [
                {
                    path: '/providers/update/:id',
                    element: <UpdateProvider />,
                },
                {
                    path: '/adverts/update/:id',
                    element: <UpdateAdvert />,
                },
                {
                    path: '/',
                    element: <DashboardComponentV3 />,
                },
            ],
        },
        /**
         * Public route
         */
        {
            element: <PublicRoute />,
            children: [
                { path: '/forgotpassword', element: <ForgotPassword /> },
                { path: '/reset-password', element: <ResetPassword /> },
                {
                    path: '/signUp',
                    element: <SignUp />,
                },
                {
                    path: '/account-validation',
                    element: <AccountValidation />,
                },
            ],
        },
        /**
         * ADMIN ROLE
         */
        {
            element: <PrivateRoute role={adminRole} />,
            children: [
                {
                    path: '/users/client/list',
                    element: <ListClient />,
                },
                {
                    path: '/users/shopmanagerV2/create',
                    element: <CreateShopManagerV2 />,
                },
                {
                    path: '/users/shopmanager/create',
                    element: <CreateShopManager />,
                },
                {
                    path: '/users/shopmanager/list',
                    element: <ListShopManager />,
                },
                {
                    path: '/users/admin/list',
                    element: <ListAdmin />,
                },
                {
                    path: '/totems',
                    element: <TotemsManagement />,
                },
            ],
        },

        {
            element: <PrivateRoute role={onlySysAdmin} />,
            children: [
                {
                    path: '/backend-settings',
                    element: <BackendSettings />,
                },
                {
                    path: '/churn',
                    element: <Churn />,
                },
            ],
        },

        /*
         * AllAdminsWithoutRoAdminRole
         */
        {
            element: <PrivateRoute role={allAdminsWithoutRoAdminRole} />,
            children: [
                {
                    path: '/providers_global/list',
                    element: <ListProvidersGlobal />,
                },
                {
                    path: '/providers_global/update/:id',
                    element: <UpdateProvidersGlobal />,
                },
                {
                    path: '/providers_global/create',
                    element: <CreateProviderGlobal />,
                },
                {
                    path: '/settings',
                    element: <Settings />,
                },
                {
                    path: '/announcements/create',
                    element: <CreateOrUpdateAnnouncement />,
                },
                {
                    path: '/announcements/edit/:id',
                    element: <CreateOrUpdateAnnouncement />,
                },
                {
                    path: '/users/client/create',
                    element: <CreateClient />,
                },
                {
                    path: '/announcements',
                    element: <Announcements />,
                },
                {
                    path: '/product_global/create',
                    element: <CreateProductGlobal />,
                },
                {
                    path: '/product_global/list',
                    element: <ListProductGlobal />,
                },
                {
                    path: '/product_global/update/:id',
                    element: <UpdateProductGlobal />,
                },

                // ===== Categories Global =====
                {
                    path: '/categories_global/create',
                    element: <CreateCategoryGlobal />,
                },
                {
                    path: '/categories_global/list',
                    element: <ListCategoryGlobal />,
                },
                {
                    path: '/categories_global/update/:id',
                    element: <UpdateCategoryGlobal />,
                },
                {
                    path: '/users/shopmanagerV2/update/:id',
                    element: <UpdateShopManagerV2 />,
                },
                {
                    path: '/users/client/update/:id',
                    element: <UpdateClient />,
                },
                {
                    path: '/users/shopmanager/update/:id',
                    element: <UpdateShopManager />,
                },
                {
                    path: '/adverts/admin/create',
                    element: <CreateAdvertAdmin />,
                },
                {
                    path: '/adverts/admin/list',
                    element: <ListAdvertAdmin />,
                },
                {
                    path: '/adverts/admin/update/:id',
                    element: <UpdateAdvertAdmin />,
                },
                {
                    path: '/users/admin/create',
                    element: <CreateAdmin />,
                },
                {
                    path: '/users/admin/update/:id',
                    element: <UpdateAdmin />,
                },
                {
                    path: '/stores',
                    element: <StoreManagement />,
                },
                {
                    path: '/charges',
                    element: <Charges />,
                },
                {
                    path: '/charges/create',
                    element: <ChargesCreateOrUpdate />,
                },
                {
                    path: '/charges/update/:id',
                    element: <ChargesCreateOrUpdate />,
                },
            ],
        },
        /**
         * MyProfileRoles
         */
        {
            element: <PrivateRoute role={myProfileRoles} />,
            children: [
                {
                    path: '/shopmanagers/users/create',
                    element: <CreateTrustee />,
                },
                {
                    path: '/shopmanagers/users/update/:id',
                    element: <UpdateTrustee />,
                },
                { path: '/wallet/balance', element: <WalletTabs /> },
                { path: '/wallet/report', element: <WalletTabs /> },
                { path: '/wallet', element: <WalletTabs /> },
                {
                    path: '/shopmanagers/users/list',
                    element: <ListTrustee />,
                },
                { path: '/accept', element: <TransferValidation /> },
                { path: '/invoice', element: <Invoice /> },
                { path: '/imports/reports', element: <ImportsReports /> },
                { path: '/nfe', element: <NfeReemission /> },
                {
                    path: '/financial/update/:id',
                    element: <UpdateFinancialRegister />,
                },
                {
                    path: '/financial',
                    element: <Financial />,
                },
                {
                    path: '/financial/create',
                    element: <CreateFinancialRegister />,
                },
                {
                    path: '/sales/list/taxregime/:id',
                    element: <TaxRegime />,
                },
                {
                    path: '/sales/list',
                    element: <ListSale />,
                },
                {
                    path: '/products/reports',
                    element: <ReportsProducts />,
                },
                {
                    path: '/stocks/list',
                    element: <ListStock />,
                },
                {
                    path: '/stocks/breakage',
                    element: <StockBreakage />,
                },
                {
                    path: '/stocks/turnover',
                    element: <StockTurnover />,
                },
                {
                    path: '/notification/logs',
                    element: <NotificationLogs />,
                },
                // ===== Notifications =====
                {
                    path: '/notifications/v2/list',
                    element: <NotificationListV2 />,
                },
                {
                    path: '/notifications/v2/create',
                    element: <CreateNotificationV2 />,
                },
                {
                    path: '/notifications/v2/edit/:id',
                    element: <UpdateNotificationV2 />,
                },

                {
                    path: '/users/data',
                    element: <ShowAndUpdateUser />,
                },
            ],
        },
        /** SHOPMANAGER ROLE */
        {
            element: <PrivateRoute role={shopManagerRole} />,
            children: [
                {
                    path: '/quick-actions',
                    element: <QuickActions />,
                },
                {
                    element: (
                        <FlagRoute flag="ENABLE_QUICK_ACTIONS_INVENTORY" />
                    ),
                    children: [
                        {
                            path: '/quick-actions/inventory',
                            element: <QuickActionsInventory />,
                        },
                        {
                            path: '/quick-actions/inventory/add',
                            element: <InventoryAdd />,
                        },
                        {
                            path: '/quick-actions/inventory/:inventoryId',
                            element: <InventoryReport />,
                        },
                    ],
                },
                {
                    path: '/quick-actions/nfe',
                    element: <QuickActionsNFE />,
                },
                {
                    path: '/quick-actions/nfe/import',
                    element: <QuickActionsImportNFE />,
                },
                {
                    path: '/planogram/automation',
                    element: <PlanogramAutomation />,
                },
                {
                    element: (
                        <FlagRoute flag="ENABLE_QUICK_ACTIONS_PLANOGRAM" />
                    ),
                    children: [
                        {
                            path: '/planogram/quick-adjust',
                            element: <PlanogramQuickAdjust />,
                        },
                    ],
                },
                {
                    path: '/stocks/adjustments',
                    element: <ListAdjustments />,
                },
                {
                    path: '/announcements/view/:id?',
                    element: <AnnouncementView />,
                },
                {
                    path: '/categories/create',
                    element: <CreateCategory />,
                },
                {
                    path: '/categories/list',
                    element: <ListCategory />,
                },
                {
                    path: '/categories/update/:id',
                    element: <UpdateCategory />,
                },

                // Products
                {
                    path: '/products/list',
                    element: <ListProduct />,
                },
                {
                    path: '/products/create',
                    element: <CreateProduct />,
                },
                {
                    path: '/products/update/:id',
                    element: <UpdateProduct />,
                },
                {
                    path: '/transfer/create',
                    element: <CreateTransfer />,
                },
                {
                    path: '/picklist/create',
                    element: <CreatePickList />,
                },
                {
                    path: '/transfer/:transferId',
                    element: <UpdateTransfer />,
                },
                {
                    path: '/transfer/picklist/:transferId',
                    element: <UpdatePicklist />,
                },
                {
                    path: '/stores/details/stock/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/sales/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/locklist/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/indicators/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/:storeId/picklist/:transferId',
                    element: <UpdatePicklist />,
                },
                {
                    path: '/stores/details/purchases/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/purchases/:id/create',
                    element: <CreatePurchase />,
                },
                {
                    path: '/stores/details/purchases/:id/update/:purchaseId',
                    element: <UpdatePurchase />,
                },
                {
                    path: '/purchase/list',
                    element: <ListPurchases />,
                },
                {
                    path: '/purchase/:purchaseId',
                    element: <UpdatePurchase />,
                },
                {
                    path: '/transfer/list',
                    element: <ListTransfer />,
                },
                {
                    path: '/stores/list',
                    element: <ListStore />,
                },

                {
                    path: '/stores/details/data/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/planogram/:id',
                    element: <StoreDetailsTabs />,
                },
                {
                    path: '/stores/details/transfers/:id',
                    element: <StoreDetailsTabs />,
                },

                {
                    path: '/stores/details/:id/transfer',
                    element: <CreateStoreTransfer />,
                },
                {
                    path: '/stores/details/:storeId/transfer/:transferId',
                    element: <UpdateTransfer />,
                },
                {
                    path: '/stores/details/:storeId/picklist-create',
                    element: <CreatePicklist />,
                },
                {
                    path: '/stores/create',
                    element: <CreateStore />,
                },
                {
                    path: '/providers/create',
                    element: <CreateProvider />,
                },
                {
                    path: '/adverts/create',
                    element: <CreateAdvert />,
                },
                {
                    path: '/providers/list',
                    element: <ListProvider />,
                },
                {
                    path: '/adverts/list',
                    element: <ListAdvert />,
                },
                {
                    path: '/shopmanager/tax-data',
                    element: <TaxDataShopManager />,
                },
                {
                    path: '/promotions',
                    element: <PromotionsAndCoupons />,
                },
                {
                    path: '/clients',
                    element: <Clients />,
                },
            ],
        },
        {
            path: '*',
            element: <Navigate to="/" replace />,
        },
    ];
};
